// @ts-nocheck
import React, { memo, useState, useMemo, useRef, useEffect, useCallback } from 'react';

interface SortOption {
	columnName: string;
	displayName: string;
}

interface StoreSelectionBoxProps {
	storeList: any[];
	storeIndex: number;
	onNavigate: (direction: number) => void;
	onSelectIndex: (index: number) => void;
	loading: boolean;
	/** Total order qty across all SKUs for the current store */
	totalOrderQty?: number;
	/** Available store-level columns to sort by (e.g., VolumeClass, Region, District) */
	sortOptions?: SortOption[];
	/** Currently active sort field (empty = default/discovery order) */
	sortField?: string;
	/** Callback when user changes the sort field */
	onSortChange?: (field: string) => void;
}

const StoreSelectionBox: React.FC<StoreSelectionBoxProps> = ({
	storeList,
	storeIndex,
	onNavigate,
	onSelectIndex,
	loading,
	totalOrderQty = 0,
	sortOptions = [],
	sortField = '',
	onSortChange,
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const [storeSearch, setStoreSearch] = useState('');
	const [dropOpen, setDropOpen] = useState(false);
	const dropRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);
	const currentStore = storeList[storeIndex] || null;

	const fieldNames = useMemo(() => {
		if (!currentStore) return {};
		const keys = Object.keys(currentStore);
		return {
			store: keys.find((k) => /^store$/i.test(k)) || keys.find((k) => /^storeid$/i.test(k)) || 'Store',
			desc:
				keys.find((k) => /desc/i.test(k) && /store/i.test(k)) ||
				keys.find((k) => /storedescription/i.test(k)) ||
				keys.find((k) => /storename/i.test(k)) ||
				keys.find((k) => /desc/i.test(k)),
		};
	}, [currentStore]);

	const storeId = currentStore?.[fieldNames.store] || '';
	const fmtNum = (n: number) => (n != null ? n.toLocaleString() : '0');

	// Build searchable list — include sort group value for section headers
	const storeOptions = useMemo(() => {
		return storeList.map((s, i) => {
			const id = s[fieldNames.store] || s.Store || s.StoreID || '';
			const desc = fieldNames.desc ? s[fieldNames.desc] || '' : '';
			const label = desc ? `${id} — ${desc}` : id || `Store ${i + 1}`;
			const groupVal = sortField ? String(s[sortField] || '(none)') : '';
			return { idx: i, id, label, groupVal };
		});
	}, [storeList, fieldNames, sortField]);

	const filteredOptions = useMemo(() => {
		if (!storeSearch) return storeOptions;
		const s = storeSearch.toLowerCase();
		return storeOptions.filter((o) => o.label.toLowerCase().includes(s) || o.groupVal.toLowerCase().includes(s));
	}, [storeOptions, storeSearch]);

	// Close dropdown on outside click
	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
		};
		document.addEventListener('mousedown', onClick);
		return () => document.removeEventListener('mousedown', onClick);
	}, []);

	useEffect(() => {
		if (dropOpen && searchRef.current) searchRef.current.focus();
	}, [dropOpen]);

	const selectStore = useCallback(
		(idx: number) => {
			onSelectIndex(idx);
			setDropOpen(false);
			setStoreSearch('');
		},
		[onSelectIndex]
	);

	const currentLabel = storeOptions[storeIndex]?.label || '—';
	const currentGroup = storeOptions[storeIndex]?.groupVal || '';

	// Get display name for active sort field
	const sortDisplayName = useMemo(() => {
		if (!sortField) return '';
		return sortOptions.find((o) => o.columnName === sortField)?.displayName || sortField;
	}, [sortField, sortOptions]);

	return (
		<div className="obs-filter-box obs-store-box">
			<div className="obs-filter-box__header" style={{ color: '#1565c0' }} onClick={() => setIsOpen(!isOpen)}>
				<span className="obs-filter-box__header-left">
					<span className={`obs-filter-box__chevron ${isOpen ? 'obs-filter-box__chevron--open' : ''}`}>
						▶
					</span>
					Store Selection
				</span>
				<span className="obs-filter-box__count">
					{storeList.length > 0 ? `${storeIndex + 1}/${storeList.length}` : '—'}
				</span>
			</div>

			{isOpen && (
				<div className="obs-filter-box__body">
					{/* Navigator */}
					<div className="obs-store-box__nav">
						<button
							className="obs-store-box__nav-btn"
							onClick={() => onNavigate(-1)}
							disabled={storeIndex <= 0 || loading}
						>
							‹
						</button>
						<div className="obs-store-box__nav-counter">
							{loading
								? 'Loading Stores...'
								: storeList.length > 0
									? `Store ${storeIndex + 1} of ${storeList.length}`
									: 'No Stores'}
						</div>
						<button
							className="obs-store-box__nav-btn"
							onClick={() => onNavigate(1)}
							disabled={storeIndex >= storeList.length - 1 || loading}
						>
							›
						</button>
						<button
							className="obs-store-box__next-btn"
							onClick={() => onNavigate(1)}
							disabled={storeIndex >= storeList.length - 1 || loading}
						>
							Next Store
						</button>
					</div>

					{/* Sort by attribute */}
					{sortOptions.length > 0 && (
						<div className="obs-store-box__sort">
							<label className="obs-store-box__sort-label">Sort by</label>
							<select
								className="obs-store-box__sort-select"
								value={sortField}
								onChange={(e) => onSortChange?.(e.target.value)}
							>
								<option value="">Default order</option>
								{sortOptions.map((opt) => (
									<option key={opt.columnName} value={opt.columnName}>
										{opt.displayName}
									</option>
								))}
							</select>
						</div>
					)}

					{/* Searchable Store dropdown */}
					<div className="obs-ss" ref={dropRef}>
						<div className="obs-ss__trigger" onClick={() => setDropOpen(!dropOpen)}>
							<span className="obs-ss__val">{currentLabel}</span>
							{sortField && currentGroup && (
								<span className="obs-ss__sort-badge" title={`${sortDisplayName}: ${currentGroup}`}>
									{currentGroup}
								</span>
							)}
							<span className="obs-ss__arrow">▾</span>
						</div>
						{dropOpen && (
							<div className="obs-ss__drop">
								<input
									ref={searchRef}
									className="obs-ss__search"
									type="text"
									placeholder={
										sortField
											? 'Search store, description, or group...'
											: 'Search store or description...'
									}
									value={storeSearch}
									onChange={(e) => setStoreSearch(e.target.value)}
									onClick={(e) => e.stopPropagation()}
								/>
								<div className="obs-ss__list" style={{ maxHeight: 260 }}>
									{filteredOptions.map((o, i) => {
										// Show group header when sort field is active and group changes
										const showGroupHeader =
											sortField &&
											!storeSearch &&
											(i === 0 || o.groupVal !== filteredOptions[i - 1]?.groupVal);
										return (
											<React.Fragment key={o.idx}>
												{showGroupHeader && (
													<div className="obs-ss__group-header">{o.groupVal}</div>
												)}
												<div
													className={`obs-ss__opt ${o.idx === storeIndex ? 'obs-ss__opt--sel' : ''}`}
													onClick={() => selectStore(o.idx)}
												>
													{o.label}
												</div>
											</React.Fragment>
										);
									})}
									{filteredOptions.length === 0 && (
										<div className="obs-ss__empty">No matching stores</div>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Store Order Metrics — Total Ordered only (no availability on Store page) */}
					<div className="obs-store-box__metrics">
						<div className="obs-store-box__metric">
							<div className="obs-store-box__metric-label">Total Ordered</div>
							<div className="obs-store-box__metric-value">{fmtNum(totalOrderQty)}</div>
							<div className="obs-store-box__metric-unit">ea</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

/**
 * Custom comparison — skip re-render when store list content, index, and metrics haven't changed.
 * Avoids deep-equal on large storeList by comparing length + first/last store ids.
 */
function arePropsEqual(prev: StoreSelectionBoxProps, next: StoreSelectionBoxProps): boolean {
	if (prev.storeIndex !== next.storeIndex) return false;
	if (prev.loading !== next.loading) return false;
	if (prev.sortField !== next.sortField) return false;
	if (prev.totalOrderQty !== next.totalOrderQty) return false;
	if (prev.onNavigate !== next.onNavigate) return false;
	if (prev.onSelectIndex !== next.onSelectIndex) return false;
	if (prev.onSortChange !== next.onSortChange) return false;

	// Fast storeList comparison: length + first/last item reference
	const pl = prev.storeList, nl = next.storeList;
	if (pl.length !== nl.length) return false;
	if (pl.length > 0 && (pl[0] !== nl[0] || pl[pl.length - 1] !== nl[nl.length - 1])) return false;

	// sortOptions rarely changes — compare by length
	if ((prev.sortOptions?.length || 0) !== (next.sortOptions?.length || 0)) return false;

	return true;
}

export default memo(StoreSelectionBox, arePropsEqual);
